window.TIV = {
  _stateKey: 'TIV_STATE',

  errorConfig: {
    invalidEmail: { type: 'warning', title: 'Invalid Email' },
    invalidPsNumber: { type: 'warning', title: 'Invalid PS/SR Number' },
    apiError: { type: 'warning', title: 'Oops, our tracking tool is down!' },
    failedLookup: { type: 'warning', title: "We couldn't find your trade-in..." },
    network: { type: 'warning', title: 'Oops, our tracking tool is down!' },
  },

  screens: {
    form: '#tiv-form',
    status: '#tiv-status',
  },

  queries: {
    acceptTransactionItem: `mutation AcceptTransactionItem($input: TransactionItemActionArgs!) {
      acceptTransactionItem(input: $input) {
        id
        status
      }
    }`,

    declineTransactionItem: `mutation DeclineTransactionItem($input: TransactionItemActionArgs!) {
      declineTransactionItem(input: $input) {
        id
        status
      }
    }`,

    trackPackingSlip: `query TrackPackingSlip($number: String!, $email: String, $accessKey: String) {
      trackPackingSlip(number: $number, email: $email, accessKey: $accessKey) {
        createdAt
        email
        id
        number
        status
        promotionCode
        totalEstimatedValue
        totalPayoutBonus
        totalPromo
        totalEstimatedPayment
        shippingLabelUrl
        trackingUrl
        expirationDate
        updatedAt
        submittedAt
        transactionItems {
          id
          number
          product {
            id
            name
            manufacturerName
            categoryName
            imageUrl
            powerUp
            lcdOK
            cracks
            scratches
          }
          status
          estimatedValue
          estimatedTotal
          actualValue
          payoutTotal
          hasEstimatedValue
          hasEstimatedTotal
          actions
          tivChangeReasons {
            name
            content
          }
          transactionAdjustments {
            id
            amount
            type
          }
          returnTrackingUrl
          createdAt
          updatedAt
        }
        shippingInformation {
          id
          firstName
          lastName
          street1
          street2
          city
          state
          zipCode
          country
          phoneNumber
        }
        paymentInformation {
          id
          payoutType
          paypalEmail
          checkAddress {
            id
            checkName
            street1
            street2
            city
            state
            zipCode
            country
            phoneNumber
          }
          checkPhoneNumber
        }
        user {
          email
          firstName
          lastName
        }
        shippingOptions {
          id
          name
        }
        selectedShippingOption {
          id
          name
        }
      }
    }`,
  },

  /**
   * Get state from sessionStorage
   */
  get state() {
    try {
      const stored = sessionStorage.getItem(this._stateKey);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('[TIV] Error reading state:', error);
      return {};
    }
  },

  /**
   * Save state to sessionStorage
   */
  set state(value) {
    try {
      sessionStorage.setItem(this._stateKey, JSON.stringify(value));
    } catch (error) {
      console.error('[TIV] Error saving state:', error);
    }
  },

  /**
   * Set state value and notify listeners
   */
  setState(key, value) {
    const currentState = this.state;
    currentState[key] = value;
    this.state = currentState;

    if (key === 'trackPackingSlip') {
      window.dispatchEvent(new CustomEvent('tiv:updated'));
    }
  },

  /**
   * Get state value
   */
  getState(key) {
    return this.state[key];
  },

  /**
   * Clear all state
   */
  clearState() {
    try {
      sessionStorage.removeItem(this._stateKey);
    } catch (error) {
      console.error('[TIV] Error clearing state:', error);
    }
  },
  
  /**
   * Get URL parameters
   */
  getUrlParams(keys) {
    const params = new URLSearchParams(window.location.search);
    const result = {};
    keys.forEach(key => {
      result[key] = params.get(key);
    });
    return result;
  },

  /**
   * Update URL parameter
   */
  updateUrlParam(key, value) {
    const url = new URL(window.location);
    url.searchParams.set(key, value);
    window.history.replaceState({}, '', url);
  },

  /**
   * Show a specific screen and hide all others
   */
  showScreen(key) {
    if (!this.screens[key]) {
      console.error(`[TIV] Invalid screen key: "${key}"`);
      return;
    }

    // Hide all screens
    Object.values(this.screens).forEach(selector => {
      const el = document.querySelector(selector);
      if (el) el.style.display = 'none';
    });

    // Show target screen
    const targetEl = document.querySelector(this.screens[key]);
    if (!targetEl) {
      console.error(`[TIV] Screen element not found: "${this.screens[key]}"`);
      return;
    }

    targetEl.style.display = 'block';
    this.updateUrlParam('tiv-screen', key);
  },

  /**
   * Initialize screen based on URL parameter or default
   */
  initScreen(defaultScreen = 'form') {
    const urlParams = this.getUrlParams(['tiv-screen']);
    const requestedScreen = urlParams['tiv-screen'];

    let screenToShow = defaultScreen;

    if (requestedScreen) {
      if (this.screens[requestedScreen]) {
        screenToShow = requestedScreen;
      } else {
        console.warn(`[TIV] Unknown screen "${requestedScreen}", showing "${defaultScreen}"`);
      }
    }

    // Guard: if trying to show status screen without state data, redirect to form
    if (screenToShow === 'status' && !this.getState('trackPackingSlip')) {
      console.warn('[TIV] No tracking data in state, redirecting to form');
      screenToShow = 'form';
      this.updateUrlParam('tiv-screen', 'form');
    }

    this.showScreen(screenToShow);
  },

  /**
   * Execute GraphQL query or mutation
   */
  async query(url, queryName, variables = {}, extraHeaders = {}) {
    if (!url) {
      throw new Error('[TIV] GraphQL URL is required');
    }

    const queryString = this.queries[queryName];
    if (!queryString) {
      throw new Error(`[TIV] Query "${queryName}" not found`);
    }

    const requestOptions = {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...extraHeaders
      }),
      redirect: 'follow',
      body: JSON.stringify({ 
        query: queryString, 
        variables: variables || null 
      })
    };

    return fetch(url, requestOptions);
  },

  /**
   * Validate email format
   */
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  },

  /**
   * Decode HTML entities
   */
  decodeHtmlEntities(html) {
    if (!html) return '';
    
    const textarea = document.createElement('textarea');
    textarea.innerHTML = html;
    let decoded = textarea.value;
    
    if (decoded.includes('&lt;') || decoded.includes('&gt;') || decoded.includes('&amp;')) {
      textarea.innerHTML = decoded;
      decoded = textarea.value;
    }
    
    return decoded;
  },

  /**
   * Format date (M/D/YYYY)
   */
  formatDate(dateString) {
    if (!dateString) return '—';

    try {
      const date = new Date(dateString);
      return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
    } catch (error) {
      return '—';
    }
  },

  /**
   * Format currency - decimal places (default: 2)
   */
  formatCurrency(amount, decimals = 2) {
    if (amount === null || amount === undefined) return '—';
    return new Intl.NumberFormat('en-US', {
      style:                 'currency',
      currency:              'USD',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(amount);
  },

  /**
   * Trigger Amazon Connect chat widget
  */
  triggerChat(e) {
    e?.preventDefault();
    const btn = document.getElementById('amazon-connect-open-widget-button');
    if (btn) btn.click();
    else console.warn('[TIV] Chat widget button not found.');
  },

  /**
   * Replace [PLACEHOLDER] tokens in text using a provided map.
   * Falls back to '—' if placeholder has no resolver or resolves to null/undefined.
   */
  resolvePlaceholders(text, placeholderMap, data) {
    if (!text) return '—';

    return text.replace(/\[(\w+)\]/g, (_, key) => {
      const resolver = placeholderMap[key.toLowerCase()];
      const value    = resolver?.(data);
      return value != null ? String(value) : '—';
    });
  },

  /**
   * Find and resolve a status message from a statuses array.
   * Replaces any [PLACEHOLDER] tokens using the provided map and data.
   */
  getStatusMessage(status, statuses, placeholderMap, data) {
    if (!status) return '—';

    const statusObj = statuses?.find(s => s.status.toLowerCase() === status.toLowerCase());
    if (!statusObj?.text) return '—';
    return TIV.resolvePlaceholders(statusObj.text, placeholderMap, data);
  },

  /**
   * Parse data-props attribute as JSON
   */
  parseProps(element) {
    try {
      const propsAttr = element.getAttribute('data-props');
      if (!propsAttr) return {};

      const decoded = TIV.decodeHtmlEntities(propsAttr);
      const cleaned = decoded.replace(/[\n\r\t]/g, '').replace(/\s+/g, ' ').trim();
      return JSON.parse(cleaned);
    } catch (error) {
      console.error('[TIV] Error parsing props:', error);
      return {};
    }
  },

  /**
  * Show toast notification
  */
  showToast(errorKey, props = {}) {
    const toast  = document.querySelector('gz-toast');
    const config = TIV.errorConfig[errorKey];
    if (!toast || !config) return;

    const rawMessage = props.errorMessages?.[errorKey];
    const decoded = rawMessage ? TIV.decodeHtmlEntities(rawMessage) : '';
    const message = TIV.resolvePlaceholders(decoded, {
      chat: () => `<a href="#open-chat-support" class="tiv-link">chat with us</a>`,
      call: () => `<a href="tel:18004293553" class="tiv-link">1-800-GAZELLE</a>`,
    });

    toast.update({ type: config.type, title: config.title, message });
    toast.show();
  },

  /**
   * Hide toast notification
   */
  hideToast() {
    const toast = document.querySelector('gz-toast');
    if (toast) toast.hide();
  },

  /**
   * Fetch and store tracking data by PS number.
   * Accepts either accessKey (marketing URL) or email (form submission) for verification.
   * Returns true on success, false on any error.
   */
  async loadFromURL(psNumber, accessKey = null, email = null) {
    const boxStatus = document.querySelector('tiv-box-status');
    if (!boxStatus) return false;

    const props = this.parseProps(boxStatus);
    const resolvedEmail = email || props.email;
    const variables = accessKey
      ? { number: psNumber, accessKey }
      : { number: psNumber, email: resolvedEmail };

    try {
      const response = await TIV.query(props.apiEndpoint, 'trackPackingSlip', variables);

      if (!response || (typeof response.ok === 'boolean' && !response.ok)) {
        console.error('[TIV] Error when fetching trackPackingSlip:', response?.status);
        TIV.showToast('failedLookup');
        return false;
      }

      const json = await response.json();

      if (!json || json.errors) {
        console.error('[TIV] GraphQL errors:', json?.errors?.map(err => err.message).join('; '));
        TIV.showToast('failedLookup');
        return false;
      }

      const trackPackingSlip = json?.data?.trackPackingSlip;
      if (!trackPackingSlip) {
        TIV.showToast('failedLookup');
        return false;
      }

      TIV.setState('trackPackingSlip', trackPackingSlip);
      TIV.setState('psNumber', psNumber);
      if (accessKey) TIV.setState('accessKey', accessKey);
      else TIV.setState('email', resolvedEmail);

      return true;

    } catch (error) {
      console.error('[TIV] Unexpected error while fetching trackPackingSlip:', error);
      TIV.showToast('failedLookup');
      return false;
    }
  },

  /**
   * Dispatch status-screen GTM analytics: box status + one event per item.
   * Pass fresh data before it reaches state; omit to read from state.
   */
  trackStatusViewed(data) {
    const ps = data || this.getState('trackPackingSlip');
    if (!ps) return;

    window.dispatchEvent(new CustomEvent('tiv:analytics', {
      detail: {
        event:      'tiv_box_status_viewed',
        ps_number:  ps.number,
        box_status: ps.status?.toLowerCase(),
        item_count: ps.transactionItems?.length || 0,
      },
    }));

    (ps.transactionItems || []).forEach((item) => {
      window.dispatchEvent(new CustomEvent('tiv:analytics', {
        detail: {
          event:          'tiv_item_status_viewed',
          ps_number:      ps.number,
          item_id:        item.id,
          item_name:      item.product?.name,
          item_status:    item.status?.toLowerCase(),
          has_adjustment: item.payoutTotal != null && item.estimatedTotal != null && item.payoutTotal !== item.estimatedTotal,
        },
      }));
    });
  },

  /**
   * Initialize TIV
   */
  init() {
    const params = new URLSearchParams(document.location.search);
    const psNumber = params.get('psnumber');
    const accessKey = params.get('accesskey');
    const emailParam = params.get('email');
    let email = null;
    if (emailParam) {
      try { email = atob(emailParam); } catch (e) { email = null; }
    }

    if (psNumber && (accessKey || email)) {
      this.initScreen('form');
      customElements.whenDefined('tiv-form').then(() => {
        const form = document.querySelector('tiv-form');
        form?.setFormLoading(true);
        this.loadFromURL(psNumber, accessKey, email).then((success) => {
          if (success) {
            this.updateUrlParam('psnumber', psNumber);
            if (accessKey) this.updateUrlParam('accesskey', accessKey);
            if (emailParam) this.updateUrlParam('email', emailParam);
            this.showScreen('status');
            this.trackStatusViewed();
          } else {
            form?.setFormLoading(false);
          }
        });
      });
    } else {
      this.initScreen('form');
      const onStatusScreen = this.getState('trackPackingSlip') &&
          params.get('tiv-screen') === 'status';
      if (onStatusScreen) {
        this.trackStatusViewed();
      } else {
        // GTM Analytics: search form viewed
        window.dispatchEvent(new CustomEvent('tiv:analytics', {
          detail: { event: 'tiv_search_viewed', source: accessKey ? 'email_link' : (email ? 'account_link' : 'direct') },
        }));
      }
    }
  }
};

// Auto initialize on DOM ready
(() => {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      TIV.init();
    });
  } else {
    TIV.init();
  }
})();

/**
 * TIV Form Web Component
 * Handles user input for tracking packing slips
 */
if (!customElements.get('tiv-form')) {
  class TivForm extends HTMLElement {
    constructor() {
      super();
      this.form = null;
      this.props = {};
      this.emailInput = null;
      this.psNumberInput = null;
      this.submitButton = null;
      this.apiEndpoint = null;
    }

    /**
     * Component mounted to DOM
     */
    connectedCallback() {
      this.props = TIV.parseProps(this);
      this.init();
    }

    /**
     * Initialize component
     */
    init() {
      this.form = this.querySelector('[data-form]');
      this.emailInput = this.querySelector('[data-email]');
      this.psNumberInput = this.querySelector('[data-ps-number]');
      this.submitButton = this.querySelector('[data-submit]');
      this.apiEndpoint = this.props.apiEndpoint;

      if (!this.form || !this.emailInput || !this.psNumberInput || !this.submitButton) {
        console.error('[TIV Form] Required form elements not found');
        return;
      }

      if (!this.apiEndpoint) {
        console.error('[TIV Form] API Endpoint URL not configured');
        return;
      }

      this.submitButton.disabled = true;

      this.form.addEventListener('submit', this.handleSubmit.bind(this));
      this.emailInput.addEventListener('input', this.validateForm.bind(this));
      this.psNumberInput.addEventListener('input', this.validateForm.bind(this));
      this.addEventListener('click', (e) => {
        if (e.target.closest('a[href="#open-chat-support"]')) TIV.triggerChat(e);
      });
    }

    /**
     * Validate form and update button state
     */
    validateForm() {
      const email = this.emailInput.value.trim();
      const psNumber = this.psNumberInput.value.trim();
      const isValid = email && psNumber && TIV.isValidEmail(email);
      this.submitButton.disabled = !isValid;
    }

    /**
     * Handle form submission
     */
    async handleSubmit(e) {
      e.preventDefault();

      const email = this.emailInput.value.trim();
      const psNumber = this.psNumberInput.value.trim();

      // GTM Analytics: lookup attempted
      window.dispatchEvent(new CustomEvent('tiv:analytics', {
        detail: {
          event: 'tiv_search_lookup_attempted',
          has_email: !!email,
          has_ps_number: !!psNumber,
        }
      }));

      if (!email || !TIV.isValidEmail(email)) {
        TIV.showToast('invalidEmail', this.props);
        return;
      }

      if (!psNumber) {
        TIV.showToast('invalidPsNumber', this.props);
        return;
      }

      await this.submitTracking(email, psNumber);
    }

    /**
     * Submit tracking request to API
     */
    async submitTracking(email, psNumber) {
      this.setFormLoading(true);
      TIV.hideToast();

      try {
        // Check if cached data exists for same email and PS number
        const cachedData = TIV.getState('trackPackingSlip');
        const cachedEmail = TIV.getState('email');
        const cachedPsNumber = TIV.getState('psNumber');

        // Use cached data if available for same inputs
        if (cachedData && cachedEmail === email && cachedPsNumber === psNumber) {
          console.log('[TIV Form] Using cached data from session storage');
          TIV.showScreen('status');
          // GTM Analytics
          window.dispatchEvent(new CustomEvent('tiv:analytics', {
            detail: { 
              event: 'tiv_search_lookup_success',
              ps_number: psNumber,
              item_count: cachedData.transactionItems?.length || 0
            },
          }));
          TIV.trackStatusViewed();
          return;
        }

        // Make API request for new or different inputs
        const response = await TIV.query(
          this.apiEndpoint,
          'trackPackingSlip',
          { number: psNumber, email: email }
        );
        const json = await response.json();

        if (json.errors) {
          console.error('[TIV Form] GraphQL errors:', json.errors.map(err => err.message).join('; '));

          // GTM Analytics: lookup error - API responded but no matching packing slip found
          window.dispatchEvent(new CustomEvent('tiv:analytics', {
            detail: {
              event: 'tiv_search_lookup_error',
              ps_number: psNumber,
              error_type: 'not_found'
            }
          }));

          // Show toast error message
          TIV.showToast('failedLookup', this.props);
          return;
        }

        const trackPackingSlip = json?.data?.trackPackingSlip;
        if (!trackPackingSlip) {

          // GTM Analytics: lookup error
          window.dispatchEvent(new CustomEvent('tiv:analytics', {
            detail: {
              event: 'tiv_search_lookup_error',
              ps_number: psNumber,
              error_type: 'not_found'
            }
          }));

          // Show toast error message
          TIV.showToast('failedLookup', this.props);
          return;
        }

        // Save to state — triggers tiv:updated → components re-render
        TIV.setState('trackPackingSlip', trackPackingSlip);
        TIV.setState('email', email);
        TIV.setState('psNumber', psNumber);

        TIV.showScreen('status');
        // GTM Analytics
        window.dispatchEvent(new CustomEvent('tiv:analytics', {
          detail: { event: 'tiv_search_lookup_success', ps_number: psNumber, item_count: trackPackingSlip.transactionItems?.length || 0 },
        }));
        TIV.trackStatusViewed(trackPackingSlip);

      } catch (error) {
        console.error('[TIV Form] Request failed:', error);

        // GTM Analytics: API failure
        window.dispatchEvent(new CustomEvent('tiv:analytics', {
          detail: {
            event: 'tiv_search_lookup_error',
            ps_number: psNumber,
            error_type: 'api_error'
          }
        }));

        // Show toast error message
        TIV.showToast('apiError', this.props);
      } finally {
        this.setFormLoading(false);
      }
    }

    /**
     * Set form loading state
     */
    setFormLoading(isLoading) {
      this.emailInput.disabled = isLoading;
      this.psNumberInput.disabled = isLoading;
      this.form.classList.toggle('loading', isLoading);

      if (isLoading) {
        this.submitButton.disabled = true;
        this.submitButton.classList.add('loading');
      } else {
        this.submitButton.classList.remove('loading');
        this.validateForm();
      }
    }
  }
  
  customElements.define('tiv-form', TivForm);
}

/**
 * TIV Box Status Web Component
 * Displays packing slip box status and contains item status cards
 */
if (!customElements.get("tiv-box-status")) {
  class TivBoxStatus extends HTMLElement {
    constructor() {
      super();
      this.props = {};
      this.trackPackingSlip = null;
    }

    /**
     * Component mounted to DOM
     */
    connectedCallback() {
      this.props = TIV.parseProps(this);
      this.itemsContainer = this.querySelector("[data-items-container]");
      this.loadData();

      window.addEventListener("tiv:updated", () => this.loadData());

      // Handle chat trigger links rendered inside status messages
      this.addEventListener('click', (e) => {
        if (e.target.closest('a[href="#open-chat-support"]')) TIV.triggerChat(e);
      });
    }

    /**
     * Component removed from DOM
     */
    disconnectedCallback() {
      window.removeEventListener("tiv:updated", this.loadData);
    }

    /**
     * Load data from TIV state
     */
    loadData() {
      this.trackPackingSlip = TIV.getState("trackPackingSlip");

      if (!this.trackPackingSlip) {
        console.warn("[TIV Box Status] No tracking data found");
        return;
      }

      this.render();
    }

    /**
     * Render box status
     */
    render() {
      if (!this.itemsContainer) return;

      const {
        number,
        status,
        createdAt,
        expirationDate,
        totalEstimatedValue,
        totalEstimatedPayment,
        totalPayoutBonus,
        totalPromo,
        transactionItems,
      } = this.trackPackingSlip;

      const { labels, statuses } = this.props;
      const upperStatus = status?.toUpperCase();
      const isCancelled = upperStatus === 'CANCELLED';
      const isConfirmed = upperStatus === 'COMPLETED' || upperStatus === 'INSPECTED';
      const hasBonuses  = totalPayoutBonus !== 0 || totalPromo !== 0;

      // Totals section — hidden entirely when status == cancelled
      const totalsHTML = isCancelled ? '' : `
        ${hasBonuses ? `
          <div class="tiv-status__label">
            <span class="align-right">${isConfirmed ? labels.confirmedValue : labels.estimatedValue}</span>
            <span class="tiv-status__value tiv-status__value--amount secondary align-right">
              ${TIV.formatCurrency(totalEstimatedValue)}
            </span>
          </div>
        ` : ''}
        ${totalPromo !== 0 ? `
          <div class="tiv-status__label">
            <span class="align-right">${labels.promotionalBonus}</span>
            <span class="tiv-status__value tiv-status__value--amount secondary align-right">
              ${TIV.formatCurrency(totalPromo)}
            </span>
          </div>
        ` : ''}
        ${totalPayoutBonus !== 0 ? `
          <div class="tiv-status__label">
            <span class="align-right">${labels.amazonBonus}</span>
            <span class="tiv-status__value tiv-status__value--amount secondary align-right">
              ${TIV.formatCurrency(totalPayoutBonus)}
            </span>
          </div>
        ` : ''}
        ${totalEstimatedPayment !== 0 && upperStatus !== 'DECLINED' ? `
          <div class="tiv-status__label">
            <span class="align-right">${isConfirmed ? labels.confirmedPayment : labels.estimatedPayment}</span>
            <span class="tiv-status__value tiv-status__value--amount align-right">
              ${TIV.formatCurrency(totalEstimatedPayment)}
            </span>
          </div>
        ` : ''}
      `;

      this.itemsContainer.innerHTML = `
        <div class="gz-grid gz-gap-4">
          <div class="gz-col-12 desktop:gz-col-4 align-left">
            <div class="tiv-status__label">Confirmation #</div>
            <div class="tiv-status__value">${number || '—'}</div>
          </div>
          <div class="gz-col-6 desktop:gz-col-4 align-left desktop:align-center">
            <div class="tiv-status__label">Created</div>
            <div class="tiv-status__value">${TIV.formatDate(createdAt)}</div>
          </div>
          <div class="gz-col-6 desktop:gz-col-4 align-right">
            <div class="tiv-status__label">Offer Expires</div>
            <div class="tiv-status__value">${TIV.formatDate(expirationDate)}</div>
          </div>
        </div>

        <div class="gz-grid gz-gap-4">
          <div class="gz-col-12 desktop:gz-col-6 align-left">
            <div class="tiv-status__label">Progress</div>
            <div class="tiv-status__value tiv-status__message">
              ${TIV.getStatusMessage(status, statuses, this.placeholderMap, this.trackPackingSlip)}
            </div>
          </div>
          <div class="gz-col-12 desktop:gz-col-6 align-right gz-col-totals">
            ${totalsHTML}
          </div>
        </div>
      `;

    }

    /**
     * Placeholder map
     * Each key matches a [PLACEHOLDER] token in status text (case-insensitive).
     * Each value is a function that receives packingSlip data and returns the resolved value.
     * Add new placeholders here as needed — no other changes required.
     */
    get placeholderMap() {
      return {
        // Count items physically received — excludes INITIATED and PENDING (not yet received)
        received: (data) => data.transactionItems.filter(i => !['INITIATED', 'PENDING'].includes(i.status?.toUpperCase())).length,
        total: (data) => data.transactionItems.length,
        chat: () => `<a href="#open-chat-support" class="tiv-link">chat with us</a>`,
        call: () => `<a href="tel:18004293553">1-800-GAZELLE</a>`,
      };
    }
  }

  customElements.define("tiv-box-status", TivBoxStatus);
}


/**
 * TIV Item Status Web Component
 * Displays individual transaction item status card
 */
if (!customElements.get("tiv-item-status")) {
  class TivItemStatus extends HTMLElement {
    constructor() {
      super();
      this.props = {};
      this.items = [];
      this.trackPackingSlip = null;
    }

    /**
     * Component mounted to DOM
     */
    connectedCallback() {
      this.props = TIV.parseProps(this);
      this.itemsContainer = this.querySelector("[data-items-container]");
      this.toast = document.querySelector("gz-toast");
      this.loadData();
      this.initEventListeners();

      window.addEventListener("tiv:updated", () => {
        this.items = [];
        this.loadData();
      });
    }

    /**
     * Component removed from DOM
     */
    disconnectedCallback() {
      window.removeEventListener("tiv:updated", this.loadData);
    }

    /**
     * Load data from TIV state
     */
    loadData() {
      this.trackPackingSlip = TIV.getState("trackPackingSlip");

      if (!this.trackPackingSlip?.transactionItems?.length) {
        console.warn("[TIV Item Status] No transaction items found");
        return;
      }

      this.render();
    }

    /**
     * Attach event listeners
     */
    initEventListeners() {
      this.addEventListener("change", (e) => {
        if (e.target.closest("[data-offer-form]") && e.target.name === "status") {
          this.handleOfferChoiceSelect(e);
        }
      });

      this.addEventListener("click", (e) => {
        if (e.target.closest("[data-offer-submit]")) this.handleOfferChoiceSubmission(e);
        if (e.target.closest('a[href="#open-chat-support"]')) TIV.triggerChat(e);
      });
    }

    /**
     * Handle radio selection — enable submit and fire GTM analytics
     */
    handleOfferChoiceSelect(e) {
      const form = e.target.closest("[data-offer-form]");
      const itemId = form.dataset.itemId;
      const item = this.items.find((i) => i.id === itemId);
      const isAccept = e.target.value === "accept";

      form.querySelector("[data-offer-submit]").disabled = false;

      // GTM Analytics: accept or return offer selected
      window.dispatchEvent(
        new CustomEvent("tiv:analytics", {
          detail: {
            event: isAccept ? "tiv_item_offer_change_confirm_selected" : "tiv_item_offer_change_return_selected",
            ps_number: TIV.getState("psNumber"),
            item_id: item.id,
            original_offer: item.estimatedTotal,
            adjusted_offer: item.payoutTotal,
          },
        })
      );
    }

    /**
     * Handle form submission — fire GTM analytics, call API, update state
     */
    async handleOfferChoiceSubmission(e) {
      e.preventDefault();

      const form = e.target.closest("[data-offer-form]");
      const submitBtn = form.querySelector("[data-offer-submit]");
      const itemId = form.dataset.itemId;
      const choice = form.querySelector('input[name="status"]:checked')?.value;
      const item = this.items.find((i) => i.id === itemId);

      if (!choice || !item) return;

      // Set loading state — consistent with tiv-form setFormLoading pattern
      const statusContainer = document.getElementById("tiv-status");
      submitBtn.disabled = true;
      submitBtn.classList.add("loading");
      statusContainer?.classList.add("loading");

      // GTM Analytics: offer change submitted
      window.dispatchEvent(
        new CustomEvent("tiv:analytics", {
          detail: {
            event: "tiv_item_offer_change_submitted",
            ps_number: TIV.getState("psNumber"),
            item_id: item.id,
            decision_made: choice,
            original_offer: item.estimatedTotal,
            adjusted_offer: item.payoutTotal,
          },
        })
      );

      try {
        const mutation = choice === "accept" ? "acceptTransactionItem" : "declineTransactionItem";
        const variables = {
          input: {
            packingSlipNumber: TIV.getState("psNumber"),
            offerNumber: item.number,
            email: TIV.getState("email"),
          },
        };

        const response = await TIV.query(this.props.apiEndpoint, mutation, variables);
        if (!response.ok) throw new Error(`HTTP error: ${response.status}`);

        const json = await response.json();
        if (json.errors) {
          throw new Error(json.errors.map((e) => e.message).join("; "));
        }

        // Re-fetch latest statuses from API — update state and re-render UI
        const psNumber = TIV.getState("psNumber");
        const accessKey = TIV.getState("accessKey");
        const email = TIV.getState("email");
        const trackVariables = accessKey
          ? { number: psNumber, accessKey }
          : { number: psNumber, email };

        const refreshResponse = await TIV.query(this.props.apiEndpoint, "trackPackingSlip", trackVariables);
        if (!refreshResponse.ok) throw new Error(`Refresh failed: ${refreshResponse.status}`);

        const refreshJson = await refreshResponse.json();
        const updated = refreshJson?.data?.trackPackingSlip;
        if (!updated) throw new Error("No data returned from refresh");

        // Clear loading state before re-render in case eventual consistency returns same status
        submitBtn.disabled = false;
        submitBtn.classList.remove("loading");
        statusContainer?.classList.remove("loading");
        // Update state with real API data — TIV.setState automatically dispatches tiv:updated
        TIV.setState("trackPackingSlip", updated);

      } catch (error) {
        console.error("[TIV Item Status] Offer submission failed:", error);
        TIV.showToast("apiError");
        // Restore button on error so user can retry
        submitBtn.disabled = false;
        submitBtn.classList.remove("loading");
        statusContainer?.classList.remove("loading");
      }
    }

    /**
     * Update a single item status in TIV state.
     * Dispatches tiv:updated — all listening components re-render.
     */
    updateItemStatus(itemId, newStatus) {
      const current = TIV.getState("trackPackingSlip");

      TIV.setState("trackPackingSlip", {
        ...current,
        transactionItems: current.transactionItems.map((item) =>
          item.id === itemId ? { ...item, status: newStatus, updatedAt: new Date().toISOString() } : item
        ),
      });

      window.dispatchEvent(new CustomEvent("tiv:updated"));
    }

    /**
     * Render all transaction items
     */
    render() {
      if (!this.itemsContainer) return;

      const { number, transactionItems } = this.trackPackingSlip;

      this.itemsContainer.innerHTML = transactionItems
        .map((item) => {
          this.items.push(item);
          return this.renderItemBox(item);
        })
        .join("");

    }

    /**
     * Render individual item box
     */
    renderItemBox(itemData) {
      const { id, number, status, product, payoutTotal, estimatedTotal, estimatedValue } = itemData;
      const isAwaiting = status?.toUpperCase() === "AWAITING_CONFIRMATION";
      const statusMessage = TIV.getStatusMessage(status, this.props.statuses, this.placeholderMap, itemData);

      return `
        <div class="tiv-status__box" data-id="${id}">
          <div class="gz-grid gz-gap-4">
            <div class="gz-col-6 desktop:gz-col-5 align-left">
              <div class="tiv-status__label">Device</div>
              <div class="tiv-status__value">${product?.name || "—"}</div>
            </div>
            <div class="gz-col-6 desktop:gz-col-1 align-right desktop:align-left">
              <div class="tiv-status__label">Offer</div>
              <div class="tiv-status__value">${TIV.formatCurrency(payoutTotal ?? estimatedTotal ?? estimatedValue)}</div>
            </div>
            <div class="gz-col-6 desktop:gz-col-2 align-left">
              <div class="tiv-status__label">ID #</div>
              <div class="tiv-status__value">${number || "—"}</div>
            </div>
            <div class="gz-col-6 desktop:gz-col-4 align-right">
              <div class="tiv-status__label">Status</div>
              <div class="tiv-status__value">${this.formatStatus(status)}</div>
            </div>
          </div>
          <div class="gz-grid">
            <div class="gz-col-12 center tiv-item-status__message">
              ${isAwaiting ? `<span class="gz-text--bold">${statusMessage}</span>` : statusMessage}
            </div>
          </div>
          ${isAwaiting ? this.offerChangeForm(itemData) : ""}
          ${isAwaiting ? this.renderChangeReasons(itemData.tivChangeReasons) : ""}
        </div>
      `;
    }

    /**
     * Render offer change form for AWAITING_CONFIRMATION items
     */
    offerChangeForm({ id, payoutTotal }) {
      return `
        <form class="gz-grid tiv-offer-adjustment-form left" data-offer-form data-item-id="${id}">
          <label class="gz-grid gz-col-12 tiv-offer-adjustment-button accept">
            <input class="hidden" type="radio" name="status" value="accept">
            <span class="gz-col-12 tiv-adjustment-header">Accept the new offer of ${TIV.formatCurrency(payoutTotal, 0)}</span>
            <span class="gz-col-12">We'll process your item and send your payment</span>
          </label>
          <label class="gz-grid gz-col-12 tiv-offer-adjustment-button decline">
            <input class="hidden" type="radio" name="status" value="decline">
            <span class="gz-col-12 tiv-adjustment-header">Return my item</span>
            <span class="gz-col-12">We'll ship your device back to you at no cost</span>
          </label>
          <button class="gz-col-12 button tiv-offer-adjustment-submit" data-offer-submit disabled type="submit">
            <span class="gz-loader"></span>
            Confirm
          </button>
        </form>
      `;
    }

    /**
     * Render "Why your offer changed" section from tivChangeReasons.
     * Filters empty content and deduplicates by exact string before rendering.
     */
    renderChangeReasons(tivChangeReasons) {
      if (!Array.isArray(tivChangeReasons) || !tivChangeReasons.length) return "";

      const seen = new Set();
      const reasonItems = tivChangeReasons.reduce((acc, { content }) => {
        const text = typeof content === "string" ? content.trim() : "";
        if (!text || seen.has(text)) return acc;
        seen.add(text);
        return acc + `<p class="tiv-change-reason__content">${text}</p>`;
      }, "");

      if (!reasonItems) return "";

      return `
        <div class="tiv-change-reason">
          <p class="tiv-change-reason__title gz-text--bold">Why your offer changed</p>
          ${reasonItems}
        </div>
      `;
    }

    /**
     * Format status text — converts SNAKE_CASE to Title Case
     */
    formatStatus(status) {
      // Override: "awaiting_response" always displays as "Contact Support"
      if (status?.toUpperCase() === "AWAITING_RESPONSE") return "Contact Support";
      if (status?.toUpperCase() === "RESPONSE_RECEIVED") return "Awaiting Re-inspection";
      return status
        ? status
            .toLowerCase()
            .replace(/_/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase())
        : "—";
    }

    /**
     * Placeholder map
     * Each key matches a [PLACEHOLDER] token in status text (case-insensitive).
     * Each value is a function receiving (itemData) and returning the resolved value.
     * Add new placeholders here as needed — no other changes required.
     */
    get placeholderMap() {
      return {
        device: (item) => `<span class="gz-text--bold">${item.product?.name}</span>`,
        amount: (item) => `<span class="gz-text--bold">${TIV.formatCurrency(item.estimatedTotal ?? item.estimatedValue)}</span>`,
        date: () =>  `<span class="gz-text--bold">${TIV.formatDate(this.trackPackingSlip.submittedAt || this.trackPackingSlip.createdAt)}</span>`,
        email: () => `<span class="gz-text--bold">${this.trackPackingSlip.email}</span>`,
        original_offer: (item) => `<span class="gz-text--primary">${TIV.formatCurrency(item.estimatedTotal, 0)}</span>`,
        adjusted_offer: (item) => `<span class="gz-text--primary">${TIV.formatCurrency(item.payoutTotal, 0)}</span>`,
        chat: () => `<a href="#open-chat-support" class="tiv-link">chat with us</a>`,
        call: () => `<a href="tel:18004293553" class="tiv-link">1-800-GAZELLE</a>`,
        tracking_number: (item) =>
          item.returnTrackingUrl
            ? `<a href="${item.returnTrackingUrl}" target="_blank" class="tiv-link">${item.returnTrackingUrl.split("tracknumbers=")[1] || item.returnTrackingUrl}</a>`
            : null,
      };
    }
  }

  customElements.define("tiv-item-status", TivItemStatus);
}